import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Mic, MicOff, X, Phone, PhoneOff, Zap, Wifi, Copy } from 'lucide-react';
import { useRealtimeChat } from '@/hooks/useRealtimeChat';
import { useRealtimeAPI } from '@/hooks/useRealtimeAPI';
import { useAuth } from '@/hooks/useAuth';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';
import { useVoiceAssistantTools } from '@/hooks/useVoiceAssistantTools';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface VoiceOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export const VoiceOverlay: React.FC<VoiceOverlayProps> = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [useRealtimeMode, setUseRealtimeMode] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get real user and brand context
  const { user } = useAuth();
  const { currentBrandId, currentBrand } = useBrandManager();
  const isMobile = useIsMobile();

  // State for real user data
  const [userGreetingData, setUserGreetingData] = useState({
    firstName: 'there',
    userName: '',
    totalTasks: 0,
    totalLeads: 0,
    brandName: 'your brand'
  });

  // Legacy WebSocket implementation
  const legacyChat = useRealtimeChat();
  
  // New Realtime API implementation with brand context
  const realtimeAPI = useRealtimeAPI();
  
  // Fetch voice assistant tools from database
  const { openAITools, isLoading: toolsLoading } = useVoiceAssistantTools();

  // Fetch real user data for greeting
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user || !currentBrandId) return;

      try {
        // Get user profile for name
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', user.id)
          .single();

        // Extract first name
        let firstName = 'there';
        let userName = '';
        if (profile?.full_name) {
          firstName = profile.full_name.split(' ')[0];
          userName = profile.full_name;
        } else if (profile?.email) {
          firstName = profile.email.split('@')[0];
          userName = profile.email;
        } else if (user.email) {
          firstName = user.email.split('@')[0];
          userName = user.email;
        }

        // Get tasks count for this week
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        weekStart.setHours(0, 0, 0, 0);

        const { data: tasksData } = await supabase
          .from('foundational_tasks')
          .select('id')
          .eq('brand_id', currentBrandId)
          .gte('created_at', weekStart.toISOString());

        // For leads, we'll check recent activity or growth squad additions
        const { data: leadsData } = await supabase
          .from('growth_squad')
          .select('id')
          .eq('brand_id', currentBrandId)
          .gte('created_at', weekStart.toISOString());

        setUserGreetingData({
          firstName,
          userName,
          totalTasks: tasksData?.length || 0,
          totalLeads: leadsData?.length || 0,
          brandName: currentBrand?.name || 'your brand'
        });

      } catch (error) {
        console.error('Error fetching user greeting data:', error);
      }
    };

    fetchUserData();
  }, [user, currentBrandId, currentBrand]);

  const startRealtimeSession = async () => {
    if (!currentBrandId || !user) {
      setError('Please select a brand first');
      return;
    }

    // Mobile permission check
    if (isMobile) {
      try {
        console.log('Requesting microphone permission for mobile...');
        await navigator.mediaDevices.getUserMedia({ 
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
        console.log('Microphone permission granted');
        
        toast({
          title: "Microphone Ready",
          description: "You can now use voice commands",
        });
      } catch (error) {
        console.error('Microphone permission denied:', error);
        setError('Microphone access is required for voice chat. Please allow microphone permissions and try again.');
        
        toast({
          title: "Microphone Access Required",
          description: "Please allow microphone permissions to use voice chat",
          variant: "destructive",
        });
        return;
      }
    }

    setIsLoading(true);
    try {
      console.log('Starting realtime session with tools:', openAITools);
      await realtimeAPI.connect(currentBrandId, user.id, openAITools);
      setRealtimeConnected(true);
      
      // Send personalized greeting with real data
      const greetingMessage = `Hello ${userGreetingData.firstName}, welcome back! You have ${userGreetingData.totalTasks} tasks this week. Your brand ${userGreetingData.brandName} has ${userGreetingData.totalLeads} new leads. How can I help you today?`;
      setTimeout(() => {
        realtimeAPI.sendTextMessage(greetingMessage);
      }, 1000);
    } catch (error) {
      console.error('Failed to start realtime session:', error);
      setError(error instanceof Error ? error.message : 'Failed to start session');
    } finally {
      setIsLoading(false);
    }
  };

  const stopRealtimeSession = () => {
    realtimeAPI.disconnect();
    setRealtimeConnected(false);
  };

  // Copy conversation to clipboard
  const copyConversation = async () => {
    const conversation = chat.messages
      .map(message => `${message.type === 'user' ? 'You' : 'AI'}: ${message.content}`)
      .join('\n\n');
    
    if (conversation.trim()) {
      try {
        await navigator.clipboard.writeText(conversation);
        toast({
          title: "Conversation copied!",
          description: "The conversation has been copied to your clipboard.",
        });
      } catch (error) {
        toast({
          title: "Copy failed",
          description: "Could not copy conversation to clipboard.",
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: "No conversation to copy",
        description: "Start a conversation first.",
        variant: "destructive",
      });
    }
  };

  // Use the selected implementation
  const chat = useRealtimeMode ? realtimeAPI : legacyChat;

  // Handle ESC key to close overlay
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, onClose]);

  // Auto-disconnect when overlay closes
  useEffect(() => {
    if (!isOpen && chat.isConnected) {
      chat.disconnect();
    }
  }, [isOpen, chat.isConnected, chat.disconnect]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-primary/95 backdrop-blur-sm"
      onWheel={(e) => e.stopPropagation()}
      style={{ touchAction: 'none' }}
    >
      <div className="h-full flex flex-col">
        {/* Header - Responsive */}
        <div className={cn(
          "flex items-center justify-between border-b border-primary-foreground/20",
          isMobile ? "p-4" : "p-6"
        )}>
          <div className="flex items-center gap-2 md:gap-3">
            <h1 className={cn(
              "font-semibold text-primary-foreground",
              isMobile ? "text-lg" : "text-2xl"
            )}>
              AI Voice Assistant
            </h1>
            {!isMobile && (
              <>
                <Badge variant={useRealtimeMode ? "default" : "secondary"} className="text-xs">
                  {useRealtimeMode ? (
                    <><Zap className="h-3 w-3 mr-1" />Realtime API</>
                  ) : (
                    <><Wifi className="h-3 w-3 mr-1" />Legacy Mode</>
                  )}
                </Badge>
                {/* Connection Status in Header */}
                <Badge 
                  variant={chat.isConnected ? "default" : "secondary"}
                  className={cn(
                    "text-xs",
                    chat.isConnected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                  )}
                >
                  {(legacyChat.isConnecting) ? "Connecting..." : chat.isConnected ? "Connected" : "Disconnected"}
                </Badge>
                {/* Disconnect icon next to Connected badge */}
                {chat.isConnected && (
                  <button
                    onClick={useRealtimeMode ? stopRealtimeSession : chat.disconnect}
                    className="text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                    title="Disconnect"
                    disabled={!currentBrandId}
                  >
                    <PhoneOff className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
          </div>
          
          {/* Mobile status indicator */}
          {isMobile && (
            <Badge 
              variant={chat.isConnected ? "default" : "secondary"}
              className={cn(
                "text-xs px-3 py-1",
                chat.isConnected ? "bg-white/20 text-white" : "bg-white/10 text-white/70"
              )}
            >
              {(legacyChat.isConnecting) ? "Connecting..." : chat.isConnected ? "Connected" : "Disconnected"}
            </Badge>
          )}
          
          <div className="flex items-center gap-2">
            {!isMobile && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setUseRealtimeMode(!useRealtimeMode)}
                className="text-primary-foreground hover:bg-primary-foreground/10"
                disabled={chat.isConnected || !currentBrandId}
              >
                Switch Mode
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <X className={cn(isMobile ? "h-5 w-5" : "h-6 w-6")} />
            </Button>
          </div>
        </div>

        {/* Main Content - Mobile Optimized */}
        <div className={cn(
          "flex-1 flex flex-col items-center justify-center space-y-4",
          isMobile ? "p-4" : "p-6"
        )}>
          {/* Status Indicators - Mobile Optimized */}
          <div className="flex flex-wrap gap-2 justify-center">
            {chat.isRecording && (
              <Badge className="bg-red-500 text-white px-3 py-1 text-sm animate-pulse">
                Listening
              </Badge>
            )}
            
            {(realtimeAPI.aiSpeaking || legacyChat.isSpeaking) && (
              <Badge className="bg-blue-500 text-white px-3 py-1 text-sm animate-pulse">
                AI Speaking
              </Badge>
            )}

            {(realtimeAPI.error || error) && (
              <Badge className="bg-red-500 text-white px-3 py-1 text-sm">
                Error: {realtimeAPI.error || error}
              </Badge>
            )}
          </div>

          {/* Central Microphone Icon - Mobile Responsive */}
          <button
            onClick={chat.isRecording ? chat.stopRecording : chat.startRecording}
            disabled={!chat.isConnected || (realtimeAPI.aiSpeaking || legacyChat.isSpeaking)}
            className={cn(
              "relative rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer",
              isMobile ? "w-20 h-20" : "w-24 h-24",
              chat.isRecording 
                ? "bg-red-500/20 border-4 border-red-500 scale-110 animate-pulse" 
                : (realtimeAPI.aiSpeaking || legacyChat.isSpeaking)
                ? "bg-blue-500/20 border-4 border-blue-500 scale-110 animate-pulse"
                : chat.isConnected
                ? "bg-primary-foreground/10 border-4 border-primary-foreground/30 hover:bg-primary-foreground/20 hover:scale-105"
                : "bg-primary-foreground/5 border-4 border-primary-foreground/20 opacity-50 cursor-not-allowed"
            )}
          >
            {chat.isRecording ? (
              <Mic className={cn(isMobile ? "w-10 h-10" : "w-12 h-12", "text-red-500")} />
            ) : (
              <MicOff className={cn(isMobile ? "w-10 h-10" : "w-12 h-12", "text-primary-foreground/70")} />
            )}
            
            {/* Voice level indicator rings */}
            {(chat.isRecording || realtimeAPI.aiSpeaking || legacyChat.isSpeaking) && (
              <>
                <div className="absolute inset-0 rounded-full border-2 border-current opacity-50 animate-ping" />
                <div className="absolute inset-4 rounded-full border-2 border-current opacity-30 animate-ping" style={{ animationDelay: '0.2s' }} />
              </>
            )}
          </button>

          {/* Live Transcript - Mobile Optimized */}
          {chat.transcript && (
            <Card className={cn(
              "w-full p-3 bg-primary-foreground/10 border-primary-foreground/20",
              isMobile ? "max-w-sm text-sm" : "max-w-2xl p-4"
            )}>
              <p className="text-primary-foreground/80 text-center italic">
                "{chat.transcript}"
              </p>
            </Card>
          )}

          {/* Control Buttons - Mobile Optimized */}
          <div className="flex gap-3">
            {!currentBrandId ? (
              <div className="text-center">
                <p className={cn(
                  "text-primary-foreground/80 mb-2",
                  isMobile ? "text-sm" : "text-base"
                )}>
                  Please select a brand to start
                </p>
                <Button
                  onClick={onClose}
                  variant="outline"
                  size={isMobile ? "sm" : "default"}
                  className="text-primary-foreground border-primary-foreground/30"
                >
                  Go to Dashboard
                </Button>
              </div>
            ) : !chat.isConnected ? (
              <Button
                onClick={useRealtimeMode ? () => startRealtimeSession() : () => chat.connect()}
                disabled={isLoading || legacyChat.isConnecting}
                size={isMobile ? "default" : "lg"}
                className={cn(
                  "bg-primary hover:bg-primary/90 text-primary-foreground",
                  isMobile ? "px-6 py-2" : "px-8 py-3"
                )}
              >
                <Phone className={cn(isMobile ? "w-4 h-4" : "w-5 h-5", "mr-2")} />
                {isLoading || legacyChat.isConnecting ? "Connecting..." : "Connect"}
              </Button>
            ) : null}
          </div>

          {/* Clear Messages Button - Mobile Optimized */}
          {chat.messages.length > 0 && (
            <Button
              onClick={chat.clearMessages}
              variant="outline"
              size="sm"
              className={cn(
                "text-primary-foreground border-primary-foreground/30",
                isMobile ? "bg-primary-foreground/10" : "hover:bg-primary-foreground/10"
              )}
            >
              Clear Conversation
            </Button>
          )}
        </div>

        {/* Conversation Transcript - Mobile Optimized with Fixed Scrolling */}
        <div className={cn(
          "border-t border-primary-foreground/20 flex-none",
          isMobile ? "p-4" : "p-6"
        )}>
          <div className="flex items-center justify-between mb-3">
            <h3 className={cn(
              "font-semibold text-primary-foreground",
              isMobile ? "text-base" : "text-lg"
            )}>
              Conversation ({chat.messages.length} messages)
            </h3>
            
            {/* Copy conversation button */}
            {chat.messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={copyConversation}
                className="text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10 p-2"
                title="Copy conversation"
              >
                <Copy className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          <div 
            className={cn(
              "bg-white/10 rounded-lg border border-primary-foreground/20 overflow-hidden",
              isMobile ? "h-48" : "h-64"
            )}
            onWheel={(e) => e.stopPropagation()}
            style={{ touchAction: 'pan-y' }}
          >
            <ScrollArea 
              className="h-full w-full"
              onWheel={(e) => e.stopPropagation()}
            >
              <div 
                className="p-3 md:p-4"
                onWheel={(e) => e.stopPropagation()}
              >
                {chat.messages.length === 0 ? (
                  <div className={cn(
                    "text-primary-foreground/60 text-center py-4",
                    isMobile ? "text-sm" : "text-base"
                  )}>
                    No messages yet. Start talking to see the conversation here.
                  </div>
                ) : (
                  <div className="space-y-2 md:space-y-3">
                    {chat.messages.map((message, index) => (
                      <div key={message.id || index} className={cn(
                        "text-primary-foreground/90 leading-relaxed",
                        isMobile ? "text-sm" : "text-base"
                      )}>
                        <span className="font-medium text-primary-foreground">
                          {message.type === 'user' ? 'You: ' : 'AI: '}
                        </span>
                        <span className="whitespace-pre-wrap">
                          {message.content}
                        </span>
                        {(message.isSpoken || message.isAudio) && (
                          <span className="text-xs text-primary-foreground/60 ml-2">(spoken)</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  );
};